/**
 * Payment Complement Service
 * Handles extraction and validation of CFDI tipo P (Pago) payment complements
 *
 * Strategy:
 * 1. Parse XML with DOMParser (handles namespaces, attribute order, encoding)
 * 2. If DOMParser fails, fall back to AI extraction via /api/extract-document
 * 3. Validate extracted data against the parent invoice
 */
import { logger } from '@/lib/logger';

import { PaymentComplementData, PaymentComplementValidation } from '../types/invoice';
import { API_BASE_URL } from '@/constants/config';

// ─── DOMParser-based extraction ──────────────────────────────────────────────

function getAttr(el: Element, name: string): string {
  return el.getAttribute(name) ?? '';
}

function queryNS(el: Element, localName: string): Element | null {
  const byTag = el.getElementsByTagNameNS('*', localName);
  if (byTag.length > 0) return byTag[0];
  return el.querySelector(localName);
}

function queryAllNS(el: Element, localName: string): Element[] {
  return Array.from(el.getElementsByTagNameNS('*', localName));
}

function extractWithDOMParser(content: string): PaymentComplementData | null {
  const parser = new DOMParser();
  const doc = parser.parseFromString(content, 'text/xml');

  const parseError = doc.querySelector('parsererror');
  if (parseError) {
    return null;
  }

  const comprobante = doc.documentElement;

  const tipoComprobante = (getAttr(comprobante, 'TipoDeComprobante') || '').toUpperCase();
  const fecha = getAttr(comprobante, 'Fecha');
  const moneda = getAttr(comprobante, 'Moneda');
  const folio = getAttr(comprobante, 'Folio') || undefined;
  const serie = getAttr(comprobante, 'Serie') || undefined;

  // Emisor
  const emisor = queryNS(comprobante, 'Emisor');
  const issuerRfc = (emisor ? getAttr(emisor, 'Rfc') : '').toUpperCase();
  const issuerName = emisor ? getAttr(emisor, 'Nombre') : undefined;

  // UUID from TimbreFiscalDigital
  let uuid = '';
  const tfd = queryNS(comprobante, 'TimbreFiscalDigital');
  if (tfd) {
    uuid = getAttr(tfd, 'UUID').toUpperCase();
  }
  const certificationDate = tfd ? getAttr(tfd, 'FechaTimbrado') : undefined;

  if (!uuid || !issuerRfc) {
    return null;
  }

  // Related UUIDs from DoctoRelacionado elements
  const relatedUuids: string[] = [];
  const doctos = queryAllNS(comprobante, 'DoctoRelacionado');
  for (const docto of doctos) {
    const id = getAttr(docto, 'IdDocumento').toUpperCase();
    if (id && /^[A-Fa-f0-9-]{36}$/i.test(id)) {
      relatedUuids.push(id);
    }
  }
  // Fallback: CfdiRelacionado
  if (relatedUuids.length === 0) {
    const cfdiRels = queryAllNS(comprobante, 'CfdiRelacionado');
    for (const rel of cfdiRels) {
      const id = getAttr(rel, 'UUID').toUpperCase();
      if (id && /^[A-Fa-f0-9-]{36}$/i.test(id)) {
        relatedUuids.push(id);
      }
    }
  }

  // Pago node details
  let paymentDate: string | undefined;
  let paymentForm: string | undefined;
  let amount: number | undefined;
  let pagoCurrency = moneda || 'XXX';
  let partialityNumber: number | undefined;
  let previousBalance: number | undefined;
  let amountPaid: number | undefined;
  let remainingBalance: number | undefined;

  const pagos = queryAllNS(comprobante, 'Pago');
  // Find the actual Pago node (not Pagos container)
  const pagoNode =
    pagos.find(p => {
      const ln = p.localName;
      return ln === 'Pago' && p.hasAttribute('FechaPago');
    }) || pagos.find(p => p.localName === 'Pago');

  if (pagoNode) {
    paymentDate = getAttr(pagoNode, 'FechaPago').split('T')[0] || undefined;
    paymentForm = getAttr(pagoNode, 'FormaDePagoP') || undefined;
    const montoStr = getAttr(pagoNode, 'Monto');
    if (montoStr) amount = parseFloat(montoStr);
    const monP = getAttr(pagoNode, 'MonedaP');
    if (monP) pagoCurrency = monP;
  }

  // DoctoRelacionado details (partiality info)
  if (doctos.length > 0) {
    const d = doctos[0];
    const partStr = getAttr(d, 'NumParcialidad');
    const prevStr = getAttr(d, 'ImpSaldoAnt');
    const paidStr = getAttr(d, 'ImpPagado');
    const remStr = getAttr(d, 'ImpSaldoInsoluto');
    if (partStr) partialityNumber = parseInt(partStr, 10);
    if (prevStr) previousBalance = parseFloat(prevStr);
    if (paidStr) amountPaid = parseFloat(paidStr);
    if (remStr) remainingBalance = parseFloat(remStr);
  }

  return {
    uuid,
    folio,
    series: serie,
    issuerRfc,
    issuerName: issuerName || undefined,
    relatedUuids,
    tipoComprobante,
    paymentDate,
    paymentForm,
    amount,
    currency: pagoCurrency === 'XXX' ? 'MXN' : pagoCurrency,
    partialityNumber,
    previousBalance,
    amountPaid,
    remainingBalance,
    issueDate: fecha ? fecha.split('T')[0] : '',
    certificationDate: certificationDate || undefined,
  };
}

// ─── AI fallback extraction ──────────────────────────────────────────────────

async function extractWithAI(xmlContent: string): Promise<PaymentComplementData | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/extract-document`, {
      credentials: 'include',
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ xmlContent, documentType: 'complement' }),
    });

    if (!response.ok) {
      logger.error('AI extraction failed', { status: response.status });
      return null;
    }

    const result = await response.json();
    if (!result.success || !result.data) return null;

    const d = result.data;
    return {
      uuid: (d.uuid || '').toUpperCase(),
      folio: d.folio || undefined,
      series: d.series || undefined,
      issuerRfc: (d.issuerRfc || '').toUpperCase(),
      issuerName: d.issuerName || undefined,
      relatedUuids: (d.relatedUuids || []).map((u: string) => u.toUpperCase()),
      tipoComprobante: (d.tipoComprobante || '').toUpperCase(),
      paymentDate: d.paymentDate || undefined,
      paymentForm: d.paymentForm || undefined,
      amount: d.amount != null ? Number(d.amount) : undefined,
      currency: d.currency || 'MXN',
      partialityNumber: d.partialityNumber != null ? Number(d.partialityNumber) : undefined,
      previousBalance: d.previousBalance != null ? Number(d.previousBalance) : undefined,
      amountPaid: d.amountPaid != null ? Number(d.amountPaid) : undefined,
      remainingBalance: d.remainingBalance != null ? Number(d.remainingBalance) : undefined,
      issueDate: d.issueDate || '',
      certificationDate: d.certificationDate || undefined,
    };
  } catch (error) {
    logger.error('AI complement extraction error', { error });
    return null;
  }
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Extract payment complement data from CFDI XML.
 * Uses DOMParser first, falls back to AI if parsing fails.
 */
export const extractComplementData = async (
  xmlFile: File
): Promise<PaymentComplementData | null> => {
  try {
    const content = await xmlFile.text();

    // Try DOMParser first
    const domResult = extractWithDOMParser(content);
    if (domResult) {
      return domResult;
    }

    // Fall back to AI
    return await extractWithAI(content);
  } catch (error) {
    logger.error('Error extracting complement data', { error });
    return null;
  }
};

/**
 * Validate payment complement against the parent invoice.
 */
export const validatePaymentComplement = (
  data: PaymentComplementData | null,
  invoiceUuid: string,
  invoiceIssuerRfc: string
): PaymentComplementValidation => {
  const errors: string[] = [];

  if (!data) {
    return {
      isValid: false,
      errors: ['No se pudo extraer la información del complemento de pago'],
    };
  }

  if (data.tipoComprobante !== 'P') {
    errors.push(
      `El tipo de comprobante debe ser "P" (Pago). Se encontró: "${data.tipoComprobante || 'vacío'}"`
    );
  }

  if (data.relatedUuids.length === 0) {
    errors.push('El complemento de pago no tiene documentos relacionados (DoctoRelacionado)');
  } else {
    const matchesParent = data.relatedUuids.some(
      u => u.toUpperCase() === invoiceUuid.toUpperCase()
    );
    if (!matchesParent) {
      errors.push(
        `El complemento no está relacionado con esta factura. ` +
          `UUID esperado: ${invoiceUuid}, Encontrados: ${data.relatedUuids.join(', ')}`
      );
    }
  }

  if (data.issuerRfc.toUpperCase() !== invoiceIssuerRfc.toUpperCase()) {
    errors.push(
      `El RFC del emisor no coincide. Esperado: ${invoiceIssuerRfc}, Encontrado: ${data.issuerRfc}`
    );
  }

  return { isValid: errors.length === 0, errors, data };
};

/**
 * Quick check if a file is a payment complement (CFDI tipo P).
 */
export const isComplementXml = async (xmlFile: File): Promise<boolean> => {
  try {
    const content = await xmlFile.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, 'text/xml');
    if (doc.querySelector('parsererror')) {
      return !!content.match(/TipoDeComprobante\s*=\s*["']P["']/i);
    }
    const tipo = doc.documentElement.getAttribute('TipoDeComprobante');
    return tipo?.toUpperCase() === 'P';
  } catch {
    return false;
  }
};
